import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { attentionHeadline } from "@/lib/kpi-labels";
import { tonePillClass } from "@/lib/tone";
import { freshnessDate, partialMonths, selectGrainSeries } from "@/lib/time-range";
import { DataIssueBanner, UpdatedStamp } from "@/components/data";

import { getOverview, getRegion, messageFor, pluginFrom, type Region } from "./api";
import { ScopeNote } from "./components/ScopeNote";
import { TopProducts } from "./components/TopProducts";
import { UnitsHeadline } from "./components/UnitsHeadline";
import { ModuleControls, useControls } from "./controls";
import {
  attentionItemsForRegion,
  chartRangeFor,
  freshnessState,
  productsFor,
  regionFromDeepLink,
  regionLabel,
  selectWindow,
  seriesBundle,
  severityTone,
  unitsSeriesBundle,
} from "./format";
import { openMonthMessage, staleMonthMessage } from "./TrafficPage";

/**
 * One market's drill-down.
 *
 * The attention items come from the OVERVIEW payload, filtered to this region,
 * because the producer emits them once for the whole run - the per-region
 * artefact carries none. Rendering nothing here would state "this market is
 * fine" using data that was never asked the question.
 */
export function RegionTab({ region, areaName }: { region: Region; areaName: string }) {
  const controls = useControls();
  const label = regionLabel(region);

  const detail = useQuery({
    queryKey: ["meteoric_units_sold", "region", region],
    queryFn: () => getRegion(region),
  });

  // The same cache entry the Overview tab and the freshness bridge use - one
  // round trip serves all three rather than three serving the same JSON.
  const overview = useQuery({
    queryKey: ["meteoric_units_sold", "overview"],
    queryFn: getOverview,
  });

  if (detail.isPending) return <div className="flex flex-col gap-6"><ModuleControls controls={controls} /><Skeleton className="h-96 w-full" /></div>;

  if (detail.isError) {
    return <div className="flex flex-col gap-6"><ModuleControls controls={controls} />
      <Alert>
        <AlertTitle>Units Sold {label} unavailable</AlertTitle>
        <AlertDescription>{messageFor(detail.error, areaName)}</AlertDescription>
      </Alert>
    </div>;
  }

  const { tile, products } = detail.data.data;
  const window = selectWindow(tile, controls.range);
  const dataThrough = detail.data.meta.data_as_of;
  const state = freshnessState(dataThrough);
  const reference = freshnessDate(dataThrough);
  const chartRange = chartRangeFor(controls.grain, controls.range);
  const growthPoints = selectGrainSeries(seriesBundle(tile), controls.grain, chartRange, reference);
  const unitsPoints = selectGrainSeries(unitsSeriesBundle(tile), controls.grain, chartRange, reference);
  const monthIssues = controls.grain === "monthly"
    ? partialMonths(seriesBundle(tile).monthly, reference).map((issue) => ({
      ...issue,
      message: issue.kind === "partial"
        ? openMonthMessage(
          issue.month,
          !growthPoints.some((point) => point.m === issue.month)
            && !unitsPoints.some((point) => point.m === issue.month)
            ? growthPoints.at(-1)?.m ?? unitsPoints.at(-1)?.m ?? null
            : null,
          { dataThrough, generatedAt: detail.data.data.generated_at },
        )
        : staleMonthMessage(issue.month),
    }))
    : [];

  const overviewPlugin = overview.data ? pluginFrom(overview.data.data) : null;
  const regionItems = overviewPlugin ? attentionItemsForRegion(overviewPlugin.attention_items, region) : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ModuleControls controls={controls} />
        <UpdatedStamp generatedAt={detail.data.data.generated_at} />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium text-ks-ink">Data through {dataThrough ?? "-"}</span>
        <Badge variant="outline" className={tonePillClass(state.tone)}>
          {state.word}
          {state.ageLabel ? ` - ${state.ageLabel}` : ""}
        </Badge>
      </div>

      {/* Raw (untrimmed) monthly bundle - see OverviewTab. */}
      {monthIssues.some((issue) => issue.kind === "partial") && (
        <ScopeNote>
          {monthIssues.filter((issue) => issue.kind === "partial").map((issue) => issue.message).join("; ")}
        </ScopeNote>
      )}
      <DataIssueBanner issues={monthIssues.filter((issue) => issue.kind === "stale")} />

      <UnitsHeadline
        tile={tile}
        window={window}
        range={controls.range}
        grain={controls.grain}
        growthPoints={growthPoints}
        unitsPoints={unitsPoints}
      />

      <TopProducts
        products={productsFor(window, products)}
        total={window.total}
        totalAll={window.total_all}
        periodLabel={window.period ?? "Period pending"}
        // The answer would be this region on every row, so the column is dropped.
        showTopRegion={false}
      />

      <Card>
        <CardContent className="flex flex-col gap-3">
          <h2 className="text-base font-semibold text-ks-ink">Attention items</h2>
          {/* Three distinct states, said in three distinct ways. "We could not
              read the run" must never render as "nothing needs attention". */}
          {overview.isError ? (
            <p className="text-sm text-muted-foreground">
              The daily run could not be read, so attention items for {label} are unknown.
            </p>
          ) : regionItems.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border">
              {regionItems.map((item) => {
                const itemRegion = regionFromDeepLink(item.deep_link);
                const content = (
                  <div className="flex min-h-11 items-center gap-3 py-2 text-sm">
                    <span
                      aria-hidden
                      className={`h-2 w-2 rounded-full ${
                        item.severity === "critical"
                          ? "bg-ks-fail"
                          : item.severity === "warn"
                            ? "bg-ks-warn"
                            : "bg-ks-grey-2"
                      }`}
                    />
                    <Badge
                      variant="outline"
                      className={`capitalize ${tonePillClass(severityTone(item.severity))}`}
                    >
                      {item.severity}
                    </Badge>
                    <span className="text-ks-ink">
                      {attentionHeadline(item.headline, tile.kpi_id, tile.kpi_name)}
                    </span>
                    {itemRegion && (
                      <ChevronRight aria-hidden className="ml-auto size-4 text-muted-foreground" />
                    )}
                  </div>
                );
                return (
                  <li key={`${item.severity}-${item.headline}`} className="hover:bg-muted">
                    {itemRegion ? (
                      <Link
                        to="/app/$area/$module/$tab"
                        params={{ area: "customer_acquisition", module: "meteoric_units_sold", tab: "units" }}
                        search={{ f_region: itemRegion }}
                        data-nonmutating
                      >
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No attention items name {label}.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
